/**
 * Investments, second edition: four figures, the positions with their
 * purchases and a purchase form in place, what the portfolio is made of, and
 * when it was bought.
 *
 * A holding row in the ledger is one purchase. The table groups purchases of
 * the same ticker (in the same currency) into one position, and a position
 * bought more than once opens to list them. Written once for both layouts
 * (`compact` is the phone).
 */

import { h } from '../vdom.js';
import { numberText, parseNumber, isAmountOnly, toBase, ASSET_CLASSES, METAL_TICKERS, TROY_OUNCE_GRAMS, METAL_PURITIES, GOLD_CARATS } from '../../../shared/model.js';
import {
  C, MONO, css, tap, input, select, emptyLine, currencyOptions, classLabel, basisPreview,
  monthPicker, quotesAllowed, planLine
} from './common.js';
import { pill, segmented } from './cashflow.js';
import { DANGER_TEXT, DANGER_BG, DANGER_LINE, GAIN_BG, GAIN_LINE, GAIN_LABEL } from './whatif.js';

/** One colour per class, the same on the row, the chips, the split and the timeline. */
export const CLASS_COLORS = {
  'Equity / ETF': '#7AA2F7',
  Crypto: '#F2A0B8',
  Retirement: '#5FD0C5',
  'Bonds / cash': '#A3D98A',
  'Real estate': '#B48EF0',
  'Precious metals': '#E3B86B',
  Cash: '#8FA3BA'
};
const colorOf = cls => CLASS_COLORS[cls] || '#8FA3BA';

const pct = ratio => (Math.max(0, Math.min(1, ratio)) * 100).toFixed(1) + '%';
const box = (compact, style, ...children) => h('div', {
  style: css({
    padding: compact ? '14px' : '18px 20px', border: `1px solid ${C.stroke}`, borderRadius: compact ? '16px' : '14px',
    background: C.card, display: 'flex', flexDirection: 'column', gap: compact ? '12px' : '16px', minWidth: 0, ...style
  })
}, ...children);
const title = (compact, text) => h('div', { style: css({ fontSize: compact ? '14px' : '15px', fontWeight: 700 }) }, text);
const hint = text => h('div', { style: css({ fontSize: '12.5px', color: C.muted }) }, text);
const mono = (text, style = {}) => h('span', { style: css({ fontFamily: MONO, ...style }) }, text);
const tone = value => (value === null ? [C.ghost, 'transparent'] : value > 0.5 ? [C.accent, GAIN_BG] : value < -0.5 ? [DANGER_TEXT, DANGER_BG] : [C.textDim, C.stroke]);
/* A result in percent with its sign, the way a money result carries one. */
const signedPct = (fmt, ratio) => (ratio > 0.0005 ? '+' : ratio < -0.0005 ? '−' : '') + fmt.percent(Math.abs(ratio), 1);
const monthLabel = (t, month) => (month ? t.months[parseInt(month.slice(5, 7), 10) - 1].toUpperCase() + ' ' + month.slice(0, 4) : null);

/**
 * The positions in the month on screen, largest first, in the base currency.
 *
 * Purchases of one ticker in one currency are one position. Cash is grouped the
 * same way, but keeps its own meaning: an amount set aside, measured against
 * inflation rather than a purchase price. A row whose currency has no rate on
 * record has no base value and is left out of the sums, never counted at par.
 */
const METAL_SYMBOLS = { XAU: 'Au', XAG: 'Ag', XPT: 'Pt', XPD: 'Pd' };

/** A fineness as it is stamped: per mille, with carats for the gold that goes by them. */
function purityLabel(code, value, fmt) {
  if (value === 1) return '1000';
  const mille = Math.round(value * 10000) / 10;
  const text = fmt.number(mille, mille % 1 ? 1 : 0);
  return code === 'XAU' && GOLD_CARATS[value] ? `${text} (${GOLD_CARATS[value]}K)` : text;
}

/** " · Ag 925" after a physical position's weight; nothing for a pure one. */
function finenessTag(row, fmt) {
  if (!row.purity || row.purity === 1) return '';
  return ' · ' + (METAL_SYMBOLS[row.ticker] || '') + ' ' + purityLabel(row.ticker, row.purity, fmt).replace(/ \(.*\)$/, '');
}

/** The form's fineness for a holding being edited: one from the list, or typed in. */
function purityUi(lot) {
  if (!lot.purity) return { purity: lot.metalForm === 'physical' ? 1 : null, purityTyped: null };
  return (METAL_PURITIES[lot.ticker] || []).includes(lot.purity)
    ? { purity: lot.purity, purityTyped: null }
    : { purity: 'other', purityTyped: String(lot.purity * 1000) };
}

export function invPositions(metrics) {
  const base = metrics.baseCurrency;
  const rates = metrics.rates || {};
  const inBase = (value, currency) => (value === null || value === undefined ? null : toBase(value, currency, base, rates));
  const groups = new Map();
  for (const row of metrics.holdRows || []) {
    /* Grams and ounces of one metal are two positions: their units do not add up. */
    const key = [String(row.ticker || '').toUpperCase(), row.currency, row.amountOnly ? 'cash' : 'held', row.unit || '', row.purity || ''].join('|');
    if (!groups.has(key)) groups.set(key, { key, ticker: String(row.ticker || '').toUpperCase(), currency: row.currency, amountOnly: row.amountOnly, unit: row.unit || null, purity: row.purity || null, lots: [] });
    groups.get(key).lots.push(row);
  }
  const list = [...groups.values()].map(group => {
    const lots = group.lots.slice().sort((a, b) => String(b.since || '').localeCompare(String(a.since || '')));
    const sum = key => lots.reduce((total, lot) => total + (Number(lot[key]) || 0), 0);
    const units = sum('units');
    const valueOwn = sum('value');
    const costOwn = sum('principal');
    const gains = lots.map(lot => lot.gain);
    const gainOwn = gains.some(gain => gain === null) ? null : sum('gain');
    const value = inBase(valueOwn, group.currency);
    const cost = inBase(costOwn, group.currency);
    const gain = inBase(gainOwn, group.currency);
    /* The class and name of the largest purchase speak for the position. */
    const lead = lots.slice().sort((a, b) => (b.value || 0) - (a.value || 0))[0];
    return {
      ...group,
      lots,
      name: lead.name,
      cls: lead.cls,
      units,
      valueOwn,
      costOwn,
      avgCost: units ? costOwn / units : 0,
      price: units ? valueOwn / units : 0,
      yieldPct: lead.yieldPct,
      value,
      cost,
      gain,
      gainPct: gainOwn === null || !costOwn ? null : gainOwn / costOwn,
      inBase
    };
  });
  const total = list.reduce((sum, row) => sum + (row.value || 0), 0);
  return list
    .map(row => ({ ...row, share: total && row.value !== null ? row.value / total : 0 }))
    .sort((a, b) => (b.value || 0) - (a.value || 0));
}

// ------------------------------------------------------------- figures ---

export function invKpis({ metrics, t, fmt, compact = false, positions }) {
  const s = t.shell;
  const k = metrics.totals;
  const rows = positions || invPositions(metrics);
  const lots = rows.flatMap(row => row.lots);
  const foreign = rows.filter(row => row.currency !== metrics.baseCurrency).reduce((sum, row) => sum + (row.value || 0), 0);
  const first = lots.map(lot => lot.since).filter(Boolean).sort()[0];
  const up = rows.filter(row => row.gain !== null && row.gain > 0.5).length;
  const down = rows.filter(row => row.gain !== null && row.gain < -0.5).length;
  const resTone = k.invGain > 0.5 ? 'up' : k.invGain < -0.5 ? 'down' : 'flat';
  const resColor = resTone === 'up' ? C.accent : resTone === 'down' ? DANGER_TEXT : C.textDim;

  const tile = (label, value, sub, { color = C.text, bg = C.card, line = C.stroke } = {}) => h('div', {
    style: css({
      padding: compact ? '12px 13px' : '16px 18px', borderRadius: '14px', minWidth: 0,
      border: `1px solid ${line}`, background: bg, display: 'flex', flexDirection: 'column', gap: compact ? '5px' : '7px'
    })
  },
    h('div', { style: css({ fontFamily: MONO, fontSize: compact ? '10px' : '10.5px', letterSpacing: '1.2px', color: C.muted }) }, label),
    h('div', { style: css({ fontFamily: MONO, fontSize: compact ? '18px' : '26px', fontWeight: 700, letterSpacing: compact ? 0 : '-0.6px', color, whiteSpace: 'nowrap' }) }, value),
    h('div', { style: css({ fontSize: compact ? '11.5px' : '12.5px', color: C.textDim, lineHeight: 1.4 }) }, sub)
  );

  return h('div', {
    style: css({ display: 'grid', gap: compact ? '10px' : '14px', gridTemplateColumns: compact ? '1fr 1fr' : 'repeat(auto-fit, minmax(210px, 1fr))' })
  },
    tile(t.marketValue, fmt.money(k.invValue), s.ivValueSub(rows.length, foreign > 0 && k.invValue ? fmt.percent(foreign / k.invValue, 0) : null)),
    tile(t.costBasis, fmt.money(k.invCost), s.ivCostSub(lots.length, first ? fmt.monthName(first) : null), { color: C.textDim }),
    tile(s.ivRes, fmt.signed(k.invGain), s.ivResSub(up, down), {
      color: resColor,
      bg: resTone === 'up' ? GAIN_BG : resTone === 'down' ? DANGER_BG : C.card,
      line: resTone === 'up' ? GAIN_LINE : resTone === 'down' ? DANGER_LINE : C.stroke
    }),
    tile(t.returnCap, fmt.percent(k.invGainPct, 1), s.ivPctSub, { color: resColor })
  );
}

// ----------------------------------------------------------- positions ---

export function invTable({ metrics, t, fmt, state, inputs, actions, compact = false, positions }) {
  const s = t.shell;
  const ui = state.invUi;
  const rows = positions || invPositions(metrics);
  const base = metrics.baseCurrency;
  const foreign = [...new Set(rows.map(row => row.currency).filter(cur => cur !== base))]
    .filter(cur => metrics.rates?.[cur])
    .map(cur => cur + ' ' + fmt.number(metrics.rates[cur], 2));
  const own = (value, currency) => fmt.money(value, Math.abs(value) >= 100 ? 0 : 2, currency);

  const startEdit = lot => {
    actions.setInvUi({
      editId: lot.id, formOpen: true, cls: lot.cls, since: lot.since || state.month, cur: null, rev: ui.rev + 1,
      metalForm: lot.metalForm || null, unit: lot.unit || null, metal: METAL_TICKERS.includes(lot.ticker) ? lot.ticker : null,
      ...purityUi(lot)
    });
    if (typeof requestAnimationFrame === 'function' && typeof document !== 'undefined') {
      requestAnimationFrame(() => document.getElementById('inv-form')?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
  };
  const iconButton = (glyph, onClick, label, danger = false) => h('div', {
    key: label, ...tap(onClick, label), class: danger ? 'pt-remove' : 'pt-button', title: label,
    style: css({
      width: '32px', height: '32px', flex: '0 0 32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
      border: danger ? 'none' : `1px solid ${C.stroke}`, color: danger ? C.faint : C.muted, fontSize: danger ? '16px' : '12px', cursor: 'pointer'
    })
  }, glyph);
  const actionsFor = row => {
    const many = row.lots.length > 1;
    const open = Boolean(ui.open[row.key]);
    return h('div', { style: css({ flex: '0 0 auto', minWidth: compact ? 0 : '104px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', marginLeft: compact ? 'auto' : 0 }) },
      many
        ? pill(s.ivLots(row.lots.length) + (open ? ' ▴' : ' ▾'), () => actions.setInvUi({ open: { ...ui.open, [row.key]: !open } }), { muted: open, key: 'lots', title: s.ivLotsTitle })
        : [iconButton('✎', () => startEdit(row.lots[0]), s.ivEditLot), iconButton('×', () => actions.askHoldingExit(row.lots[0].id), s.ivRemoveLot, true)]
    );
  };
  const result = (gain, gainPct, currency, size = '14px') => {
    const [color, bg] = tone(gain);
    return h('div', { style: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }) },
      mono(gain === null ? s.inflationMissing : fmt.signed(gain, currency), { fontSize: size, fontWeight: 700, color, whiteSpace: 'nowrap' }),
      gainPct !== null && mono(signedPct(fmt, gainPct), { key: 'p', padding: '1px 6px', borderRadius: '5px', background: bg, fontSize: '11px', fontWeight: 700, color })
    );
  };

  const asset = row => h('div', { style: css({ flex: compact ? '1 1 160px' : '1 1 220px', minWidth: 0, display: 'flex', alignItems: 'center', gap: '12px' }) },
    h('span', { style: css({ flex: '0 0 4px', width: '4px', height: '34px', borderRadius: '2px', background: colorOf(row.cls) }) }),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }) },
      h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }) },
        mono(row.ticker, { fontSize: '14px', fontWeight: 700, letterSpacing: '.3px' }),
        h('span', {
          style: css({ padding: '2px 7px', borderRadius: '5px', background: C.panel, border: `1px solid ${C.stroke}`, fontSize: '11px', color: colorOf(row.cls), whiteSpace: 'nowrap' })
        }, classLabel(t, row.cls))),
      h('span', { style: css({ fontSize: '12.5px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, row.name + finenessTag(row, fmt)))
  );
  const unitsCell = row => h('div', { style: css({ flex: compact ? '1 1 120px' : '0 0 140px', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }) },
    row.amountOnly
      ? [mono(s.cashAmount, { key: 'a', fontSize: '13px', color: C.textDim }), mono(row.yieldPct ? s.ivYieldLine(fmt.number(row.yieldPct)) : s.cashNoYield, { key: 'y', fontSize: '11.5px', color: C.muted })]
      : [
        mono(row.unit ? fmt.number(row.units) + ' ' + s.ivUnitAbbr[row.unit] : s.ivUnits(fmt.number(row.units)), { key: 'u', fontSize: '13px' }),
        mono(s.ivAvg(own(row.avgCost, row.currency)) + (row.unit ? '/' + s.ivUnitAbbr[row.unit] : ''), { key: 'c', fontSize: '11.5px', color: C.muted })
      ]
  );
  const priceCell = row => h('div', { style: css({ flex: '0 0 100px', textAlign: 'right', fontFamily: MONO, fontSize: '13px', color: C.textDim }) },
    row.amountOnly ? '—' : own(row.price, row.currency) + (row.unit ? '/' + s.ivUnitAbbr[row.unit] : ''));
  const valueCell = row => h('div', { style: css({ flex: compact ? '0 0 auto' : '0 0 110px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }) },
    mono(row.value === null ? s.ivNoRate : fmt.money(row.value), { fontSize: '15px', fontWeight: 700, whiteSpace: 'nowrap', color: row.value === null ? C.gold : C.text }),
    row.currency !== base && mono(own(row.valueOwn, row.currency), { key: 'o', fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' }));
  const shareCell = row => h('div', { style: css({ flex: compact ? '1 1 140px' : '1 1 110px', minWidth: '90px', display: 'flex', flexDirection: compact ? 'row' : 'column', alignItems: compact ? 'center' : 'stretch', gap: compact ? '10px' : '6px' }) },
    h('div', { style: css({ flex: compact ? 1 : 'none', height: '6px', borderRadius: '3px', background: C.stroke, overflow: 'hidden' }) },
      h('div', { style: css({ width: pct(row.share), height: '100%', borderRadius: '3px', background: colorOf(row.cls) }) })),
    mono(fmt.percent(row.share, 1), { fontSize: '11.5px', color: C.textDim }));

  const lotRow = (row, lot) => {
    const cost = row.inBase(lot.principal, lot.currency);
    const gain = row.inBase(lot.gain, lot.currency);
    const [color] = tone(gain);
    return h('div', {
      key: lot.id,
      style: css({
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: compact ? '4px 12px' : '6px 20px', minHeight: '44px',
        padding: '6px 0 6px 16px', background: ui.editId === lot.id ? C.line : 'transparent'
      })
    },
      mono(monthLabel(t, lot.since) || s.ivNoMonth, { flex: '0 0 90px', fontSize: '11.5px', letterSpacing: '.8px', color: C.muted }),
      h('span', { style: css({ flex: '1 1 160px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }) },
        mono(lot.amountOnly ? own(lot.principal, lot.currency) : fmt.number(lot.units) + (lot.unit ? ' ' + s.ivUnitAbbr[lot.unit] : '') + ' × ' + own(lot.cost, lot.currency), { fontSize: '12.5px' }),
        lot.until && mono(s.soldIn(fmt.monthName(lot.until)), { key: 'sold', fontSize: '10.5px', color: C.danger })),
      !compact && mono(cost === null ? '—' : fmt.money(cost), { key: 'cost', flex: '0 0 110px', textAlign: 'right', fontSize: '12.5px', color: C.textDim }),
      mono(gain === null ? '—' : fmt.signed(gain), { flex: compact ? '0 0 auto' : '0 0 110px', textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color }),
      h('div', { style: css({ flex: '0 0 auto', display: 'flex', gap: '4px', marginLeft: compact ? 'auto' : 0 }) },
        iconButton('✎', () => startEdit(lot), s.ivEditLot),
        iconButton('×', () => actions.askHoldingExit(lot.id), s.ivRemoveLot, true))
    );
  };

  const line = row => {
    const open = row.lots.length > 1 && ui.open[row.key];
    const editing = row.lots.length === 1 && ui.editId === row.lots[0].id;
    return h('div', {
      key: row.key,
      style: css({ display: 'flex', flexDirection: 'column', borderBottom: `1px solid ${C.line}`, background: editing ? C.line : 'transparent' })
    },
      compact
        /* Two columns on a phone, whatever its width: what it is and what it is worth, then how much and how it did, then its share. */
        ? h('div', { style: css({ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center', gap: '10px 12px', padding: '12px 2px' }) },
          asset(row), valueCell(row), unitsCell(row), result(row.gain, row.gainPct, base, '13px'), shareCell(row), actionsFor(row))
        : h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 20px', padding: '12px 4px' }) },
          asset(row), unitsCell(row), priceCell(row), valueCell(row), shareCell(row),
          h('div', { style: css({ flex: '0 0 110px', display: 'flex', justifyContent: 'flex-end' }) }, result(row.gain, row.gainPct, base)),
          actionsFor(row)),
      open && h('div', {
        key: 'lots', class: 'pt-rise',
        style: css({ display: 'flex', flexDirection: 'column', margin: compact ? '0 0 12px 6px' : '0 4px 12px 20px', borderLeft: `2px solid ${C.stroke}` })
      }, ...row.lots.map(lot => lotRow(row, lot)))
    );
  };

  const head = (text, flex, align = 'left') => h('span', { style: css({ flex, textAlign: align }) }, text);

  return box(compact, { gap: '6px', padding: compact ? '14px' : '16px 20px 18px' },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px 12px', flexWrap: 'wrap', paddingBottom: '8px' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 200px', minWidth: 0 }) },
        title(compact, s.ivPositions),
        hint(s.ivPricesSub(base, foreign.join(' · ')))),
      quotesAllowed(state)
        ? h('div', { key: 'quotes', style: css({ display: 'flex', gap: '8px', flexWrap: 'wrap', opacity: state.busy ? 0.5 : 1, ...(compact ? { flex: '1 1 100%' } : {}) }) },
          pill(t.backfillPrices, actions.backfillPrices, { key: 'hist', title: s.ivHistTitle }),
          pill('↻ ' + t.refreshPrices, actions.refreshPrices, { primary: true, key: 'refresh', title: s.pricesNote }))
        : h('div', { key: 'plan', style: css({ flex: '1 1 260px' }) }, planLine(s.planQuotesNote, { spent: true }))
    ),
    !compact && rows.length > 0 && h('div', {
      key: 'head', class: 'pt-inv-head',
      style: css({ display: 'flex', alignItems: 'center', gap: '10px 20px', padding: '0 4px 8px', fontFamily: MONO, fontSize: '10px', letterSpacing: '1.2px', color: C.muted })
    },
      head(s.ivColAsset, '1 1 220px'), head(s.ivColUnits, '0 0 140px'), head(s.ivColPrice, '0 0 100px', 'right'),
      head(s.ivColValue, '0 0 110px', 'right'), head(s.ivColShare, '1 1 110px'), head(s.ivColRes, '0 0 110px', 'right'), head('', '0 0 104px')),
    rows.length === 0
      ? h('div', { key: 'empty', style: css({ padding: '8px 0' }) }, emptyLine(s.ivEmpty))
      : h('div', { key: 'list', style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.ghost}` }) }, ...rows.map(line)),
    (ui.formOpen || ui.editId)
      ? invForm({ t, fmt, state, inputs, actions, compact, positions: rows })
      : h('div', {
        key: 'open', ...tap(() => actions.setInvUi({ formOpen: true, editId: null, cls: 'Equity / ETF', since: state.month, cur: null, metalForm: null, unit: null, metal: null, purity: null, purityTyped: null, rev: ui.rev + 1 }), s.ivNew), class: 'pt-button',
        style: css({
          marginTop: '12px', minHeight: '64px', padding: '12px 16px', border: `1px dashed ${C.strokeHi}`, borderRadius: '12px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', textAlign: 'center', cursor: 'pointer'
        })
      },
        mono(s.ivNew, { fontSize: '12.5px', fontWeight: 700, letterSpacing: '1.4px', color: C.accent }),
        h('span', { style: css({ fontSize: '12.5px', color: C.muted }) }, s.ivNewHint))
  );
}

/**
 * A new purchase, or the one whose ✎ was pressed. A ticker already held joins
 * its position; a price typed in is the position's price, so it is written to
 * every purchase of it -- the next refresh would do the same.
 */
function invForm({ t, fmt, state, inputs, actions, compact, positions }) {
  const s = t.shell;
  const ui = state.invUi;
  const editing = ui.editId ? (state.data?.holdings || []).find(row => row.id === ui.editId) : null;
  const cls = ui.cls || (editing ? editing.cls : 'Equity / ETF');
  const cash = isAmountOnly(cls);
  const since = ui.since || (editing ? editing.since : null) || state.month;
  const read = name => parseNumber(inputs.value(name));
  const close = () => actions.setInvUi({ formOpen: false, editId: null, rev: ui.rev + 1 });
  /* Followed as it is picked, so the sums written out under the fields are in the right money. */
  const currencyNow = ui.cur || (editing ? editing.currency : actions.entryCurrency());
  const basis = basisPreview({ inputs, fmt, t, unitsField: 'ivUnits', costField: 'ivCost', priceField: 'ivPrice', hintField: 'ivBasis', currency: currencyNow });

  /*
   * Precious metals come as paper -- a ticker priced per share -- or as the
   * metal itself, weighed in grams or troy ounces. Physical metal is priced
   * from the metal's quote, which is per ounce; the cost of a purchase is
   * filled in from what the metal cost in the month it was bought.
   */
  const metal = cls === 'Precious metals';
  /*
   * A holding from before metals had a form says nothing: one with a metal's
   * ticker was always priced per ounce of that metal (physical, in ounces);
   * anything else was a ticker of its own (paper). Never grams by default --
   * that would quietly divide an edited holding's value by 31.
   */
  const legacyForm = editing && !editing.metalForm ? (METAL_TICKERS.includes(editing.ticker) ? 'physical' : 'paper') : null;
  const physical = metal && (ui.metalForm || editing?.metalForm || legacyForm || 'physical') === 'physical';
  const weight = physical ? (ui.unit || editing?.unit || (editing ? 'oz' : 'g')) : null;
  const metalCode = physical ? (ui.metal || (editing && METAL_TICKERS.includes(editing.ticker) ? editing.ticker : 'XAU')) : null;
  /*
   * Fineness: chosen from the metal's usual ones, or typed in per mille. A new
   * piece starts at investment grade; one being edited keeps what it had
   * (none recorded -- an older entry -- stays pure).
   */
  const presets = physical ? METAL_PURITIES[metalCode] : [];
  const purityPick = !physical ? null
    : ui.purity ?? (editing ? (editing.purity ? (presets.includes(editing.purity) ? editing.purity : 'other') : 1) : presets[0]);
  const typedPurity = purityPick === 'other' ? parseNumber(ui.purityTyped ?? (editing?.purity ? String(editing.purity * 1000) : '')) : null;
  const purity = !physical ? null
    : purityPick === 'other' ? (typedPurity > 0 && typedPurity <= 1000 ? typedPurity / 1000 : null)
      : purityPick;
  const factor = (weight === 'g' ? 1 / TROY_OUNCE_GRAMS : 1) * (purity || 1);
  const per = weight ? s.ivUnitAbbr[weight] : null;
  const quoteKey = physical ? [metalCode, since, currencyNow, weight].join('|') : null;
  const quote = ui.metalQuote && ui.metalQuote.key === quoteKey ? ui.metalQuote : null;
  if (physical && quotesAllowed(state) && !quote) actions.loadMetalPrice({ key: quoteKey, ticker: metalCode, month: since, currency: currencyNow, unit: weight });
  /*
   * The month's price, for the piece's fineness, goes into an empty cost box
   * once per metal, month, currency, unit and fineness -- never over a figure typed in.
   */
  const piecePrice = quote?.status === 'ok' && purity ? Math.round(quote.price * purity * 100) / 100 : null;
  const fillKey = quoteKey + '|' + purity;
  if (physical && piecePrice !== null && !editing && typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      const node = inputs.node('ivCost');
      /* Empty, or still holding the figure filled in for another month: never a figure typed in. */
      if (!node || node.dataset.auto === fillKey || (node.value && node.value !== node.dataset.autoValue)) return;
      node.value = numberText(piecePrice);
      node.dataset.auto = fillKey;
      node.dataset.autoValue = node.value;
      basis.onInput();
    });
  }
  /*
   * Editing: "price now" was filled in per the unit and fineness the form
   * opened with. Switch either -- or the metal -- and that figure means
   * something else, so a price nobody has touched follows the switch (or
   * empties, for another metal) rather than being saved under the new unit.
   */
  const seededPrice = () => (editing && physical && metalCode === editing.ticker ? money(Number(editing.price) * factor) : '');
  if (editing && physical && typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      const node = inputs.node('ivPrice');
      if (!node) return;
      const shape = metalCode + '|' + factor;
      if (node.dataset.shape === undefined) { node.dataset.shape = shape; node.dataset.seed = node.value; return; }
      if (node.dataset.shape === shape) return;
      if (node.value === node.dataset.seed) {
        node.value = seededPrice() || '';
        node.dataset.seed = node.value;
        basis.onInput();
      }
      node.dataset.shape = shape;
    });
  }

  const save = () => {
    const ticker = physical ? metalCode : inputs.value('ivTicker').trim().toUpperCase();
    if (!ticker) return actions.showError(s.tickerNeeded);
    if (physical && !purity) return actions.showError(s.ivPurityNeeded);
    const currency = inputs.value('ivCur') || currencyNow;
    const stored = physical && purity !== 1 ? purity : null;
    const held = positions.find(row => row.ticker === ticker && row.currency === currency && row.amountOnly === cash && (row.unit || null) === weight && (row.purity || null) === stored);
    const name = inputs.value('ivName').trim() || (held ? held.name : '') || (physical ? s.ivMetals[ticker] : ticker);
    let fields;
    if (cash) {
      const amount = read('ivAmount');
      const rate = read('ivYield');
      if (amount === null || amount < 0) return actions.showError(s.amountNeeded);
      if (rate !== null && rate < 0) return actions.showError(s.numberNeeded);
      fields = { ticker, name, cls, currency, units: 1, cost: amount, price: amount, yieldPct: rate || 0, since };
    } else {
      const units = read('ivUnits');
      const cost = read('ivCost');
      const typedPrice = read('ivPrice');
      if (units === null || cost === null || units <= 0 || cost < 0 || (typedPrice !== null && typedPrice < 0)) return actions.showError(s.numberNeeded);
      let price;
      if (physical) {
        /* Stored per troy ounce, as the metal is quoted, whatever the holding is weighed in. */
        const same = (state.data?.holdings || []).find(row => row.ticker === ticker && row.currency === currency && row.metalForm === 'physical' && row.id !== ui.editId);
        /* The price the form opened with, untouched: kept exactly, not re-derived through a rounded per-gram figure. */
        const untouched = typedPrice !== null && seededPrice() !== '' && Math.abs(typedPrice - parseNumber(seededPrice())) < 1e-9;
        price = untouched ? Number(editing.price)
          : typedPrice ? typedPrice / factor
          : (same ? Number(same.price) : null) || (editing && editing.ticker === ticker ? Number(editing.price) : null)
            || (quote?.status === 'ok' ? quote.perOunce : null) || cost / factor;
      } else {
        price = typedPrice || (held ? held.price : null) || (editing && editing.ticker === ticker ? Number(editing.price) : null) || cost;
      }
      fields = {
        ticker, name, cls, currency, units, cost, price, yieldPct: 0, since,
        metalForm: metal ? (physical ? 'physical' : 'paper') : null, unit: physical ? weight : null,
        /* Pure is stored as none, like every entry from before fineness existed. */
        purity: stored
      };
      /* One price per position: the other purchases of it take a price typed in here. */
      if (typedPrice && physical && (!editing || Math.abs(price - Number(editing.price)) > 1e-9)) {
        /* Metal: every physical lot of it in this currency, grams or ounces -- the stored price is per ounce for all. */
        for (const lot of state.data?.holdings || []) {
          if (lot.id !== ui.editId && lot.ticker === ticker && lot.currency === currency && lot.metalForm === 'physical' && Math.abs(Number(lot.price) - price) > 1e-9) actions.saveEdit('holding', lot.id, { price });
        }
      } else if (typedPrice && held) {
        for (const lot of held.lots) if (lot.id !== ui.editId && Math.abs(Number(lot.price) - typedPrice) > 1e-9) actions.saveEdit('holding', lot.id, { price: typedPrice });
      }
    }
    if (editing) actions.saveEdit('holding', editing.id, fields);
    else actions.addHolding(fields);
    inputs.clear(['ivTicker', 'ivName', 'ivUnits', 'ivCost', 'ivPrice', 'ivAmount', 'ivYield']);
    close();
  };

  const money = value => (value === null || value === undefined || value === '' ? undefined : numberText(Math.round(Number(value) * 1e6) / 1e6));
  const label = text => h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, text);
  const field = (name, text, value, { unit = null, placeholder = '0', number = true, span = 1, onInput = null, upper = false } = {}) => h('label', {
    key: name,
    style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0, ...(span > 1 ? { gridColumn: 'span ' + span } : {}) })
  },
    label(text),
    h('div', {
      style: css({ display: 'flex', alignItems: 'center', gap: '6px', height: '42px', border: `1px solid ${C.strokeHi}`, borderRadius: '10px', background: C.panel, padding: '0 12px' })
    },
      input(inputs, name, placeholder, {
        type: number ? 'number' : 'text', value, onEnter: save, onInput,
        style: {
          flex: 1, minWidth: 0, background: 'transparent', border: 'none', padding: 0,
          fontFamily: number || upper ? MONO : 'inherit', fontSize: number || upper ? '14px' : '13.5px',
          fontWeight: upper ? 700 : 400, textTransform: upper ? 'uppercase' : 'none'
        }
      }),
      unit && mono(unit, { key: 'u', fontSize: '11.5px', color: C.faint }))
  );
  const chip = key => {
    const on = key === cls;
    return h('div', {
      key, ...tap(() => actions.setInvUi({ cls: key }), classLabel(t, key)), role: 'radio', 'aria-checked': on ? 'true' : 'false',
      style: css({
        height: '32px', display: 'flex', alignItems: 'center', gap: '7px', padding: '0 12px', borderRadius: '8px', cursor: 'pointer',
        border: `1px solid ${on ? colorOf(key) : C.stroke}`, background: on ? C.line : 'transparent', fontSize: '12.5px', color: on ? C.text : C.textDim
      })
    }, h('span', { style: css({ width: '8px', height: '8px', borderRadius: '2px', background: colorOf(key) }) }), classLabel(t, key));
  };
  const grid = (...children) => h('div', { style: css({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${compact ? 130 : 150}px, 1fr))`, gap: '10px' }) }, ...children);
  const amountSeed = editing ? (isAmountOnly(editing.cls) ? editing.price : Number(editing.units) * Number(editing.price)) : null;

  return h('div', {
    key: 'invform-' + (editing ? editing.id : 'new') + '-' + ui.rev,
    id: 'inv-form', class: 'pt-rise',
    style: css({
      marginTop: '12px', padding: '14px', borderRadius: '12px', background: C.panel,
      border: `1px solid ${editing ? C.goldDeep : C.stroke}`, display: 'flex', flexDirection: 'column', gap: '12px'
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px' }) },
      mono(editing ? s.ivFormEdit : s.ivFormNew, { fontSize: '10.5px', letterSpacing: '1.2px', color: editing ? C.gold : C.muted }),
      h('div', { style: css({ flex: 1 }) }),
      h('div', {
        ...tap(close, s.cfClose), class: 'pt-button', title: s.cfClose,
        style: css({ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', color: C.muted, fontSize: '16px', cursor: 'pointer' })
      }, '×')),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '6px' }) },
      label(s.ivClass),
      h('div', { role: 'radiogroup', style: css({ display: 'flex', flexWrap: 'wrap', gap: '6px' }) }, ...ASSET_CLASSES.map(chip))),
    metal && h('div', { key: 'metal', style: css({ display: 'flex', flexWrap: 'wrap', gap: '10px 16px', alignItems: 'flex-end' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }) },
        label(s.ivFormL),
        segmented([['physical', s.ivPhysical], ['paper', s.ivPaper]].map(([value, text]) => ({ value, label: text })),
          physical ? 'physical' : 'paper', value => actions.setInvUi({ metalForm: value }), { key: 'form' })),
      physical && h('div', { key: 'which', style: css({ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }) },
        label(s.ivMetalL),
        segmented(METAL_TICKERS.map(code => ({ value: code, label: s.ivMetals[code] })), metalCode, value => actions.setInvUi({ metal: value, purity: null, purityTyped: null }), { key: 'which' })),
      physical && h('div', { key: 'unit', style: css({ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }) },
        label(s.ivWeightL),
        segmented([['g', s.ivGrams], ['oz', s.ivOunces]].map(([value, text]) => ({ value, label: text })), weight, value => actions.setInvUi({ unit: value }), { key: 'unit' }))),
    physical && h('div', { key: 'purity', style: css({ display: 'flex', flexWrap: 'wrap', gap: '10px 16px', alignItems: 'flex-end' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }) },
        label(s.ivPurityL),
        segmented([
          ...(presets.includes(purityPick) || purityPick === 'other' ? [] : [{ value: purityPick, label: purityLabel(metalCode, purityPick, fmt) }]),
          ...presets.map(value => ({ value, label: purityLabel(metalCode, value, fmt) })),
          { value: 'other', label: s.ivPurityOther }
        ], purityPick, value => actions.setInvUi({ purity: value }), { key: 'purity-' + metalCode })),
      purityPick === 'other' && h('div', { key: 'typed', style: css({ width: '150px' }) },
        field('ivPurity', s.ivPurityF, editing?.purity ? fmt.number(editing.purity * 1000, 1) : undefined, {
          placeholder: '900', unit: '‰', onInput: event => actions.setInvUi({ purityTyped: event?.target?.value ?? inputs.value('ivPurity') })
        }))),
    grid(
      !physical && field('ivTicker', s.ivTicker, editing ? editing.ticker : undefined, { placeholder: metal ? s.ivPaperTickerPh : s.ivTickerPh(cls), number: false, upper: true }),
      field('ivName', s.ivName, editing ? editing.name : undefined, { placeholder: physical ? s.ivMetalNamePh : metal ? s.ivPaperNamePh : s.ivNamePh(cls), number: false, span: physical || !compact ? 2 : 1 })),
    grid(
      ...(cash
        ? [
          field('ivAmount', s.ivAmountF, amountSeed === null ? undefined : money(amountSeed)),
          field('ivYield', s.ivYieldF, editing && editing.yieldPct ? money(editing.yieldPct) : undefined, { unit: '%' })
        ]
        : [
          field('ivUnits', physical ? s.ivWeightF : s.ivUnitsF, editing ? money(editing.units) : undefined, { placeholder: numberText('0.0000'), unit: physical ? per : s.ivUnitShort, onInput: basis.onInput }),
          field('ivCost', physical ? s.ivCostPer(per) : s.ivCostF, editing ? money(editing.cost) : undefined, { placeholder: numberText('0.00'), onInput: basis.onInput }),
          /* Shown per gram for a holding weighed in grams; stored per ounce -- see save. */
          field('ivPrice', physical ? s.ivPricePer(per) : s.ivPriceF, editing ? money(physical ? Number(editing.price) * factor : editing.price) : undefined, { placeholder: s.ivPricePh, onInput: basis.onInput })
        ]),
      h('label', { key: 'cur', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
        label(s.ivCurF),
        select(inputs, 'ivCur', currencyOptions(state, currencyNow), { height: '42px', borderRadius: '10px', fontFamily: MONO, fontSize: '13px' },
          { value: currencyNow, onChange: cur => actions.setInvUi({ cur }) })),
      h('div', { key: 'since', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
        label(s.ivSinceF),
        monthPicker({
          id: 'inv-since', value: since, onPick: stamp => actions.setInvUi({ since: stamp }),
          label: s.ivSinceF, title: s.heldSinceTitle, note: cash ? s.heldSinceNoteCash : s.heldSinceNote, mobile: compact,
          state, actions, t, fmt,
          triggerStyle: {
            height: '42px', display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: '10px',
            border: `1px solid ${C.strokeHi}`, background: C.panel, fontSize: '13px', color: C.text, letterSpacing: '.6px'
          }
        }))
    ),
    !cash && h('div', { key: 'basis' }, basis.hint),
    physical && h('div', { key: 'mq', style: css({ fontSize: '12px', lineHeight: 1.5, color: quote?.status === 'ok' ? C.accentHi : C.muted }) },
      !quotesAllowed(state) ? s.ivMetalManual
        : !quote || quote.status === 'pending' ? s.ivMetalLoading
          : quote.status === 'ok' ? s.ivMetalHint(s.ivMetalsOf[metalCode], fmt.monthName(since), fmt.money(quote.price, 2, currencyNow) + '/' + per,
            ...(purity && purity !== 1 ? [purityLabel(metalCode, purity, fmt), fmt.money(piecePrice, 2, currencyNow) + '/' + per] : []))
            : quote.status === 'rate' ? s.ivMetalNoRate(fmt.monthName(since))
              : s.ivMetalMissing),
    h('div', { style: css({ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }) },
      h('div', { style: css({ flex: '1 1 300px', fontSize: '12px', lineHeight: 1.5, color: C.muted }) }, cash ? s.cashNote : physical ? s.ivPhysicalNote : s.ivNote),
      h('div', {
        ...tap(close, s.cfCancel),
        style: css({ height: '40px', display: 'flex', alignItems: 'center', padding: '0 14px', borderRadius: '9px', color: C.muted, fontSize: '13px', cursor: 'pointer' })
      }, s.cfCancel),
      pill(editing ? s.cfSaveChanges : s.ivAdd, save, { primary: true, key: 'save' }))
  );
}

// -------------------------------------------------------------- classes ---

export function invClasses({ metrics, t, fmt, compact = false, positions }) {
  const s = t.shell;
  const rows = positions || invPositions(metrics);
  const total = rows.reduce((sum, row) => sum + (row.value || 0), 0);
  if (!rows.length) return null;
  const classes = ASSET_CLASSES.map(key => {
    const own = rows.filter(row => row.cls === key && row.value !== null);
    if (!own.length) return null;
    const value = own.reduce((sum, row) => sum + row.value, 0);
    const measured = own.filter(row => row.gain !== null && row.cost);
    const cost = measured.reduce((sum, row) => sum + row.cost, 0);
    const gain = measured.reduce((sum, row) => sum + row.gain, 0);
    return { key, value, share: total ? value / total : 0, tickers: own.map(row => row.ticker).join(' · '), gainPct: cost ? gain / cost : null, gain };
  }).filter(Boolean).sort((a, b) => b.value - a.value);

  return box(compact, {},
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px' }) }, title(compact, s.ivClasses), hint(s.ivClassesSub)),
    h('div', { style: css({ display: 'flex', height: '14px', borderRadius: '7px', overflow: 'hidden', gap: '2px', background: C.stroke }) },
      ...classes.map(row => h('div', { key: row.key, title: classLabel(t, row.key) + ' ' + fmt.percent(row.share, 1), style: css({ width: pct(row.share), height: '100%', background: colorOf(row.key) }) }))),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.ghost}` }) },
      ...classes.map(row => h('div', {
        key: row.key,
        style: css({ display: 'grid', gridTemplateColumns: '12px minmax(0,1fr) auto auto', gap: '6px 14px', alignItems: 'center', minHeight: '52px', padding: '6px 0', borderBottom: `1px solid ${C.line}` })
      },
        h('span', { style: css({ width: '10px', height: '10px', borderRadius: '3px', background: colorOf(row.key) }) }),
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
          h('span', { style: css({ fontSize: '13.5px', fontWeight: 600 }) }, classLabel(t, row.key)),
          h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, row.tickers)),
        h('div', { style: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }) },
          mono(fmt.money(row.value), { fontSize: '13.5px', fontWeight: 700, whiteSpace: 'nowrap' }),
          mono(fmt.percent(row.share, 1), { fontSize: '11.5px', color: C.muted })),
        mono(row.gainPct === null ? '—' : signedPct(fmt, row.gainPct), { minWidth: compact ? '52px' : '64px', textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: tone(row.gainPct === null ? null : row.gain)[0] })
      )))
  );
}

// ------------------------------------------------------------- timeline ---

export function invTimeline({ metrics, t, fmt, compact = false, positions }) {
  const s = t.shell;
  const rows = positions || invPositions(metrics);
  if (!rows.length) return null;
  const months = new Map();
  for (const row of rows) {
    for (const lot of row.lots) {
      const key = lot.since || '';
      if (!months.has(key)) months.set(key, []);
      months.get(key).push({ row, lot, amount: row.inBase(lot.principal, lot.currency) });
    }
  }
  /* Newest first; purchases with no month on record go last rather than vanish. */
  const order = [...months.keys()].sort((a, b) => (!a ? 1 : !b ? -1 : b.localeCompare(a)));

  return box(compact, {},
    h('div', { style: css({ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 200px' }) }, title(compact, s.ivWhen), hint(s.ivWhenSub)),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }) },
        mono(fmt.money(metrics.totals.invCost), { fontSize: '13px', fontWeight: 700 }),
        h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, s.ivPaidIn))),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '14px' }) },
      ...order.map((key, i) => {
        const items = months.get(key);
        const sum = items.reduce((total, item) => total + (item.amount || 0), 0);
        return h('div', { key: 'm' + (key || 'none'), style: css({ display: 'grid', gridTemplateColumns: compact ? '74px minmax(0,1fr)' : '86px minmax(0,1fr)', gap: '12px' }) },
          h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '3px', paddingTop: '4px' }) },
            mono(monthLabel(t, key) || s.ivNoMonth, { fontSize: '12px', fontWeight: 700, letterSpacing: '1px', color: i === 0 ? C.accent : C.textDim }),
            mono(fmt.money(sum), { fontSize: '11.5px', color: C.muted })),
          h('div', { style: css({ display: 'flex', flexWrap: 'wrap', gap: '6px', paddingLeft: '12px', borderLeft: `2px solid ${i === 0 ? '#2B6B57' : C.stroke}` }) },
            ...items.map(item => h('div', {
              key: item.lot.id,
              style: css({ display: 'flex', alignItems: 'center', gap: '8px', height: '34px', padding: '0 10px', borderRadius: '8px', background: C.panel, border: `1px solid ${C.stroke}` })
            },
              h('span', { style: css({ width: '6px', height: '6px', borderRadius: '50%', background: colorOf(item.row.cls) }) }),
              mono(item.row.ticker, { fontSize: '12px', fontWeight: 700 }),
              mono(item.amount === null ? '—' : fmt.money(item.amount), { fontSize: '11.5px', color: C.muted })))));
      }))
  );
}

/** The tab, for either layout. */
export function investTab(ctx) {
  const positions = invPositions(ctx.metrics);
  const all = { ...ctx, positions };
  const classes = invClasses(all);
  const timeline = invTimeline(all);
  return h('div', { class: 'pt-inv', style: css({ display: 'flex', flexDirection: 'column', gap: ctx.compact ? '12px' : '16px' }) },
    invKpis(all),
    invTable(all),
    (classes || timeline) && h('div', {
      key: 'pair',
      style: css({ display: 'grid', gap: ctx.compact ? '12px' : '14px', gridTemplateColumns: ctx.compact ? 'minmax(0,1fr)' : 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', alignItems: 'start' })
    }, classes, timeline)
  );
}
